import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Select, Button, Form, Tag } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

import { Controller, useCreateTicket } from '../hooks/useCreateTicket';
import CustomAutoCompleteField from '@components/newUI/CustomAutoComplete';
import { TICKET_PRIORITY_LABEL } from '../utilTicket';
import { ITicketTag, TTicketPriority } from '@models/SupportTicket';
import TextInputSelect from '@components/newUI/TextInputSelect';
import ModalViewImages from '@pages/OrganizedSales/CreateOrders/layoutContent/ModalUpdateImages/ModalViewImages';

import { useWatch } from 'react-hook-form';
import { SUPPORT_BRANCH_LABELS } from '@components/ui/CskhQuickFilter';
import { PriorityBadge } from '../components/detailCpn/PriorityBadge';
import { Composer } from '../components/detailCpn/Composer';
// import LocationField, { validateLocationCoord } from '../components/LocationField';

const { TextArea } = Input;

export interface ICategoryOption {
  id: number;
  name: string;
  /** Tên nhóm hỗ trợ chứa loại này (TicketCreateModal truyền sẵn) */
  tagName?: string;
}

interface ITicketCreateContentProps {
  categories: ICategoryOption[]; // dữ liệu chọn nơi nào cần dùng thì trueyèn vô dùm, cảm ơn!!
  tags: ITicketTag[]; // dữ liệu chọn nơi nào cần dùng thì trueyèn vô dùm, cảm ơn!!
  onClose: () => void;
  onCreated?: () => void | Promise<void>;
}

const ALL_TAGS = -1; // giá trị của option "Tất cả" trong select nhóm (không gửi backend: tagIds chỉ để lọc)

// Tiêu đề mục: chữ thường, đậm, màu navy như header app + 1 đường kẻ mảnh (không bọc card từng phần)
const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h4 className="mb-2 pb-1.5 border-b border-[#d6e4ff] text-[14px] font-semibold text-[#0e1f33]">{children}</h4>
);

export default function TicketCreateContent({ categories, tags, onClose, onCreated }: ITicketCreateContentProps) {
  const {
    control,
    errors,
    submitting,
    onSubmit,
    customerLoading,
    customerOptions,
    onSelectCustomer,
    setValue,
    getValues
  } = useCreateTicket({
    onCreated: async () => {
      await onCreated?.();
      onClose();
    }
  });

  const tagIds = useWatch({ control, name: 'tagIds' });
  const allTagIds = useMemo(() => (tags || []).map(t => t.id), [tags]);
  const allTagsSelected = allTagIds.length > 0 && (tagIds || []).length >= allTagIds.length;

  // Mặc định chọn tất cả nhóm: lúc tags tải xong và sau khi tạo phiếu (hook reset tagIds = []).
  // Người dùng tự bỏ hết thì giữ trống (trống cũng = hiện mọi loại yêu cầu).
  const userClearedTags = useRef(false);
  useEffect(() => {
    if (allTagIds.length && !(tagIds || []).length && !userClearedTags.current) setValue('tagIds', allTagIds);
  }, [allTagIds, tagIds]);

  // điều khiển lại danh sách loại yêu cầu (categories)
  const categoryOptions = useMemo(() => {
    const allCategories = categories || [];
    const tagList = tags || [];

    // Lọc theo từ 2 nhóm trở lên (hoặc tất cả) thì gắn tên nhóm cạnh mỗi loại để biết loại thuộc nhóm nào
    const showGroup = !tagIds?.length || tagIds.length > 1;
    const toOption = (c: ICategoryOption) => ({
      value: c.id,
      chipLabel: c.name, // chữ hiện trên ô đã chọn
      searchText: `${c.name} ${c.tagName || ''}`, // gõ tên nhóm cũng tìm ra
      label:
        showGroup && c.tagName ? (
          <div className="flex items-center justify-between gap-2">
            <span className="truncate">{c.name}</span>
            <span className="shrink-0 rounded bg-gray-100 px-1.5 text-[11px] leading-[18px] text-gray-600">
              {c.tagName}
            </span>
          </div>
        ) : (
          c.name
        )
    });

    // không chọn tag, hoặc chọn hết tag -> hiện tất cả category
    const showAll = !tagIds?.length || tagIds.length >= tagList.length;
    if (showAll) {
      return allCategories.map(toOption);
    }

    const allowedIds = new Set<number>();
    tagList.filter(t => tagIds.includes(t.id)).forEach(t => (t.categories || []).forEach(c => allowedIds.add(c.id)));

    return allCategories.filter(c => allowedIds.has(c.id)).map(toOption);
  }, [tags, categories, tagIds]);

  // khi đổi tag: bỏ những category đã chọn mà không còn nằm trong danh sách lọc
  const handleTagChange = (newTagIds: number[]) => {
    userClearedTags.current = newTagIds.length === 0;
    setValue('tagIds', newTagIds);

    const tagList = tags || [];
    const showAll = !newTagIds.length || newTagIds.length >= tagList.length;
    if (showAll) return;

    const allowedIds = new Set<number>();
    tagList.filter(t => newTagIds.includes(t.id)).forEach(t => (t.categories || []).forEach(c => allowedIds.add(c.id)));

    const current = getValues('categoryIds') || [];
    const kept = current.filter(id => allowedIds.has(id));
    if (kept.length !== current.length) setValue('categoryIds', kept, { shouldValidate: true });
  };

  // Select nhóm có option "Tất cả" (ALL_TAGS) đứng đầu, giống select nhóm ở header.
  // Khi chọn hết: "Tất cả" + từng nhóm đều được tích (ô chỉ hiện chip "Tất cả"), nên vẫn bỏ được từng nhóm.
  // - bấm "Tất cả" -> chọn hết; bỏ "Tất cả" -> trống
  // - đang chọn hết mà bỏ 1 nhóm -> còn các nhóm kia (vd 3 nhóm bỏ 1 -> còn 2)
  // - tự tích đủ từng nhóm -> "Tất cả" cũng được tích
  const onTagSelectChange = (vals: number[]) => {
    const hasAll = vals.includes(ALL_TAGS);
    if (hasAll && !allTagsSelected) {
      handleTagChange(allTagIds);
      return;
    }
    if (!hasAll && allTagsSelected && vals.length >= allTagIds.length) {
      handleTagChange([]); // vừa bỏ tích "Tất cả"
      return;
    }
    const picked = vals.filter(v => v !== ALL_TAGS);
    handleTagChange(picked.length >= allTagIds.length ? allTagIds : picked);
  };

  // Có tệp CSKH thì Nội dung xử lý bắt buộc (server lưu nội dung + tệp vào resolveContent)
  const staffFilesWatch = useWatch({ control, name: 'staffFiles' });
  const needResolveNote = (staffFilesWatch || []).some(f => !!f.originFileObj);
  const [preview, setPreview] = useState<{ images: { url: string; name: string }[]; index: number } | null>(null);

  return (
    <Form layout="vertical" onFinish={onSubmit} className="[&_.ant-form-item]:!mb-3 [&_.ant-form-item-label]:!pb-1">
      {preview && (
        <ModalViewImages
          isOpen
          listHinh={preview.images}
          images1={preview.images[preview.index]}
          base64={preview.images[preview.index]?.url}
          initialIndex={preview.index}
          callBackClose={() => setPreview(null)}
        />
      )}

      {/* 1 khung chung 2 cột: trái = khách & yêu cầu, phải = xử lý ban đầu. Không bọc card từng phần. */}
      <div
        className="grid grid-cols-1 lg:grid-cols-5 rounded-md border border-[#d6e4ff] overflow-hidden"
        // 1240px: cột phải đủ rộng cho 2 nút đính kèm + chữ "Tối đa 50MB" cùng 1 hàng
        style={{ width: 'min(1240px, 95vw)' }}
      >
        {/* ================= TRÁI: khách hàng & yêu cầu ================= */}
        <section className="lg:col-span-3 p-3 bg-white">
          <SectionTitle>Khách hàng &amp; yêu cầu</SectionTitle>
          <div className="grid grid-cols-12 gap-x-2">
            <Form.Item
              className="col-span-12 md:col-span-6"
              label="Tên khách hàng (nhập để tìm kiếm)"
              required
              validateStatus={errors.customerName ? 'error' : ''}
              help={errors.customerName?.message}
            >
              <Controller
                name="customerName"
                control={control}
                rules={{ required: 'Vui lòng nhập tên khách hàng!' }}
                render={({ field: { value, onChange, onBlur, ref } }) => (
                  <CustomAutoCompleteField
                    isAutoComplete
                    serverSearch
                    loading={customerLoading}
                    value={value}
                    onChange={(val: string) => {
                      onChange(val);
                      setValue('customerUserName', ''); // xoá username khi change ô input
                    }}
                    onBlur={onBlur}
                    inputRef={ref}
                    optionsAutoComplete={customerOptions}
                    onSelect={onSelectCustomer}
                    placeHolder="VD: A Hùng, hung.adsun hoặc 0909000111"
                    allowClear
                  />
                )}
              />
            </Form.Item>

            <Form.Item
              className="col-span-6 md:col-span-3"
              label="Tài khoản"
              validateStatus={errors.customerUserName ? 'error' : ''}
              help={errors.customerUserName?.message}
            >
              <Controller
                name="customerUserName"
                control={control}
                // disabled={true} // để này thì value ko đổ vào form !! ta đặt disabled vào input
                render={({ field }) => <Input disabled {...field} placeholder="Tự điền khi chọn khách" />}
              />
            </Form.Item>

            <Form.Item
              className="col-span-6 md:col-span-3"
              label="Số điện thoại"
              required
              validateStatus={errors.contactNumber ? 'error' : ''}
              help={errors.contactNumber?.message}
            >
              <Controller
                name="contactNumber"
                control={control}
                rules={{
                  required: 'Vui lòng nhập số điện thoại!',
                  pattern: { value: /^[0-9]{9,11}$/, message: 'Số điện thoại không hợp lệ!' }
                }}
                render={({ field }) => <Input {...field} placeholder="VD: 0999999999" />}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-2">
            <Form.Item label="Nhóm hỗ trợ">
              <Controller
                name="tagIds"
                control={control}
                render={({ field }) => (
                  <Select
                    mode="multiple"
                    allowClear
                    maxTagCount="responsive"
                    placeholder="Chọn để lọc loại yêu cầu"
                    // Chọn hết: tích cả "Tất cả" lẫn từng nhóm trong danh sách, nhưng ô chỉ hiện chip "Tất cả"
                    value={allTagsSelected ? [ALL_TAGS, ...allTagIds] : field.value}
                    onChange={onTagSelectChange}
                    tagRender={({ label, value, closable, onClose }) =>
                      allTagsSelected && value !== ALL_TAGS ? (
                        <></>
                      ) : (
                        <Tag
                          closable={closable}
                          onClose={onClose}
                          onMouseDown={e => e.preventDefault()}
                          className="my-0.5 mr-1"
                        >
                          {label}
                        </Tag>
                      )
                    }
                    options={[
                      { label: 'Tất cả', value: ALL_TAGS },
                      ...(tags || []).map(t => ({ label: t.name, value: t.id }))
                    ]}
                    optionFilterProp="label"
                  />
                )}
              />
            </Form.Item>

            <Form.Item
              label="Loại yêu cầu"
              required
              validateStatus={errors.categoryIds ? 'error' : ''}
              help={errors.categoryIds?.message}
            >
              <Controller
                name="categoryIds"
                control={control}
                rules={{ validate: v => (v && v.length > 0) || 'Vui lòng chọn ít nhất 1 loại yêu cầu!' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    mode="multiple"
                    maxTagCount="responsive"
                    placeholder="Chọn loại yêu cầu"
                    options={categoryOptions}
                    optionLabelProp="chipLabel"
                    optionFilterProp="searchText"
                    onChange={field.onChange}
                  />
                )}
              />
            </Form.Item>
          </div>

          <Form.Item
            label="Biển số xe"
            required
            validateStatus={errors.vehiclePlates ? 'error' : ''}
            help={errors.vehiclePlates?.message}
          >
            <Controller
              name="vehiclePlates"
              control={control}
              rules={{ validate: v => (v && v.length > 0) || 'Nhập ít nhất 1 biển số xe!' }}
              render={({ field }) => (
                <TextInputSelect value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
              )}
            />
          </Form.Item>

          <Form.Item label="Mô tả (khách cung cấp)">
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextArea {...field} autoSize={{ minRows: 2, maxRows: 5 }} placeholder="VD: Camera không tín hiệu" />
              )}
            />
          </Form.Item>

          {/* Tệp của khách nằm ngay dưới mô tả: cùng là dữ liệu khách cung cấp. Gửi lên qua CustomerFiles (useCreateTicket).
              Dùng lại khung Composer của tệp CSKH (không có ô nhập chữ) nên giống hệt bên CSKH: ảnh / video / Word / PDF /
              Excel, tổng tệp khách ≤ 50MB (riêng với 50MB của tệp CSKH), ô thu nhỏ bấm xem, × bỏ từng tệp, "Bỏ hết", dán ảnh
              Ctrl+V. Tắt dòng "Khách hàng đọc được…" vì đây là tệp khách gửi, không phải nội dung gửi khách. */}
          <Form.Item label="Tệp khách gửi" className="!mb-0">
            <Controller
              name="files"
              control={control}
              render={({ field }) => (
                <Composer
                  newFiles={field.value || []}
                  setNewFiles={field.onChange}
                  onPreviewImage={(images, index) => setPreview({ images, index })}
                  sendLabel="Tạo phiếu"
                  showNotice={false}
                />
              )}
            />
          </Form.Item>
        </section>

        {/* ================= PHẢI: xử lý ban đầu (không bắt buộc) ================= */}
        <section className="lg:col-span-2 p-3 bg-[#f0f5ff] lg:border-l border-[#d6e4ff] flex flex-col">
          <SectionTitle>CSKH xử lý</SectionTitle>
          <div className="grid grid-cols-2 gap-x-2">
            <Form.Item
              label="Công ty"
              required
              validateStatus={errors.systemBranch ? 'error' : ''}
              help={errors.systemBranch?.message}
            >
              <Controller
                name="systemBranch"
                rules={{ required: 'Vui lòng chọn công ty!' }}
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    className="w-full"
                    placeholder="Chọn công ty"
                    options={Object.keys(SUPPORT_BRANCH_LABELS).map(code => ({
                      value: code,
                      label: SUPPORT_BRANCH_LABELS[code]
                    }))}
                  />
                )}
              />
            </Form.Item>

            <Form.Item label="Độ ưu tiên">
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <Select {...field} className="w-full">
                    {([0, 1, 2, 3] as TTicketPriority[]).map(p => (
                      <Select.Option key={p} value={p}>
                        <PriorityBadge p={p}>{TICKET_PRIORITY_LABEL[p]}</PriorityBadge>
                      </Select.Option>
                    ))}
                  </Select>
                )}
              />
            </Form.Item>
          </div>

          <Form.Item
            label="Danh sách đơn (nếu có)"
            tooltip="Nhập một hoặc nhiều mã đơn rồi nhấn Enter. Cách bằng dấu phẩy (,). VD: 260929001, 260929002."
          >
            <Controller
              name="orderCodes"
              control={control}
              render={({ field }) => (
                <TextInputSelect
                  placehd="Nhập các mã đơn (cách nhau dấu phẩy)"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </Form.Item>

          {/* Nội dung xử lý + tệp CSKH chung 1 khung (giống ô nhập ở chi tiết phiếu): tệp chỉ đi kèm nội dung */}
          <Form.Item
            label="Nội dung xử lý"
            required={needResolveNote}
            className="!mb-0"
            extra={
              <span className="text-[12px]">
                Có tệp thì phải nhập nội dung; phiếu tạo ra sẽ ở trạng thái Đang xử lý.
              </span>
            }
          >
            <Controller
              name="staffFiles"
              control={control}
              render={({ field: filesField }) => (
                <Composer
                  newFiles={filesField.value || []}
                  setNewFiles={filesField.onChange}
                  onPreviewImage={(images, index) => setPreview({ images, index })}
                  sendLabel="Tạo phiếu"
                >
                  <Controller
                    name="resolveNote"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        {...field}
                        bordered={false}
                        aria-label="Nội dung xử lý"
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        placeholder="Nội dung đã xử lý? (dán ảnh bằng Ctrl+V)"
                        className="text-[13px]"
                      />
                    )}
                  />
                </Composer>
              )}
            />
          </Form.Item>
        </section>
      </div>

      <div className="flex justify-end gap-2 pt-3">
        <Button onClick={onClose} disabled={submitting}>
          Đóng
        </Button>
        {/* Bỏ nút "Nhập lại" (23/10/2026): nằm sát "Tạo phiếu", bấm nhầm là mất hết dữ liệu đã nhập */}
        <Button
          type="primary"
          htmlType="submit"
          loading={submitting}
          icon={<UploadOutlined style={{ transform: 'translateY(-3px)' }} />}
        >
          Tạo phiếu hỗ trợ
        </Button>
      </div>
    </Form>
  );
}
